import { Link } from "react-router-dom";
import { Wrench, Building2 } from "lucide-react";
import Seo from "../components/site/Seo";
import { PageHero, Section, Timeline, Disclaimer } from "../components/site/Bits";
import { MOD_STEPS } from "../components/site/data";
import DynamicForm from "../components/forms/DynamicForm";
import { useSite } from "../lib/site";
import { mediaUrl } from "../lib/api";

const MINOR = ["Grab rails", "Handrails", "Portable and modular ramps", "Handheld showers", "Lever taps", "Door handles", "Step modifications"];
const COMPLEX = ["Bathroom redesign", "Step-free and level access", "Doorway widening", "Kitchen modifications", "Threshold removal"];

function Gallery({ items }) {
  if (!items?.length) return <p className="text-ink-muted">Before and after photos coming soon.</p>;
  return (
    <ul className="grid gap-8 md:grid-cols-2" data-testid="mods-gallery">
      {items.map((g, i) => (
        <li key={i} className="card overflow-hidden">
          <div className="grid grid-cols-2">
            <figure className="relative"><img src={mediaUrl(g.before)} alt={`Before: ${g.title}`} loading="lazy" className="aspect-square w-full object-cover" /><figcaption className="absolute left-3 top-3 rounded-full bg-surface px-3 py-1 text-sm font-bold">Before</figcaption></figure>
            <figure className="relative"><img src={mediaUrl(g.after)} alt={`After: ${g.title}`} loading="lazy" className="aspect-square w-full object-cover" /><figcaption className="absolute left-3 top-3 rounded-full bg-teal px-3 py-1 text-sm font-bold text-on-teal">After</figcaption></figure>
          </div>
          <div className="p-5"><h3 className="font-display text-lg font-semibold">{g.title}</h3>{g.caption && <p className="mt-1 text-ink-muted">{g.caption}</p>}</div>
        </li>
      ))}
    </ul>
  );
}

export default function HomeMods() {
  const { content } = useSite();
  return (
    <>
      <Seo title="NDIS Home Modifications Melbourne" path="/home-modifications"
        description="Minor and complex NDIS home modifications in Melbourne and Victoria: grab rails, ramps, bathroom redesign, level access and doorway widening, by qualified, insured trades." />
      <PageHero eyebrow="Home modifications" title="Make your home safer and easier to live in"
        intro="From a single grab rail to a full bathroom redesign, we assess, quote, document and complete home modifications with qualified, insured tradespeople."
        image="/images/doorway-wheelchair.jpg" imageAlt="A woman in a wheelchair at the back door of her brick home">
        <a href="#assessment-form" className="btn-primary" data-testid="mods-request-assessment-btn">Request a Home Assessment</a>
        <Link to="/referrers#referral-form" className="btn-outline">Make a Referral</Link>
      </PageHero>
      <Section eyebrow="What we do" title="Minor and complex modifications">
        <div className="grid gap-6 lg:grid-cols-2">
          {[{ icon: Wrench, title: "Minor modifications", list: MINOR, body: "Lower-cost changes that can usually be completed quickly, often with a simpler assessment and quote." },
            { icon: Building2, title: "Complex modifications", list: COMPLEX, body: "Structural or larger changes that usually need an OT home modification assessment, a detailed scope of works and building approvals." }].map((b) => (
            <section key={b.title} className="card p-7" data-testid={`mods-${b.title.split(" ")[0].toLowerCase()}`}>
              <b.icon className="text-teal" size={32} aria-hidden="true" />
              <h3 className="mt-4 font-display text-2xl font-semibold">{b.title}</h3>
              <p className="mt-2 text-ink-muted">{b.body}</p>
              <ul className="mt-5 grid gap-2 sm:grid-cols-2">{b.list.map((x) => <li key={x} className="rounded-xl bg-sand px-4 py-3 font-medium">{x}</li>)}</ul>
            </section>
          ))}
        </div>
      </Section>
      <Section id="process" tone="bg-surface" eyebrow="Our process" title="How home modifications work">
        <Timeline steps={MOD_STEPS} testid="mods-timeline" />
      </Section>
      <Section id="gallery" eyebrow="Our work" title="Before and after">
        <Gallery items={content.gallery?.items} />
      </Section>
      <Section id="assessment-form" tone="bg-surface" eyebrow="Get started" title="Request a home assessment"
        intro="Tell us about your home and what you need. Renting? That's fine, we'll note landlord consent and help with the information your landlord needs.">
        <div className="max-w-4xl"><DynamicForm kind="homemod" testid="homemod-form" /><div className="mt-8"><Disclaimer /></div></div>
      </Section>
    </>
  );
}
